import {
  lazy,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSession } from 'next-auth/react';
import { useForm } from 'react-hook-form';

import { PHOTO_BOOTH_CHAR_LIMIT } from '@lessonPlanner/constants';
import { Language } from '@lessonPlanner/enums';

import { streamChatCompletion } from '../api/external/GPT';
import { Button } from '../components/Button';
import { SelectField, TextAreaField } from '../components/form-fields';
import GenerateButton from '../components/GenerateButton';
import GeneratePremiumConfirmationModal from '../components/GeneratePremiumConfirmationModal';
import GenericBanner from '../components/GenericBanner';
import {
  ErrorDetails,
  ErrorModalDetails,
  ErrorWithCode,
} from '../components/organisms/ErrorModalDetails';
import ModalAlerts from '../components/organisms/Modal/ModalAlerts';
import PopUpModal from '../components/PopUpModal';
import SEO from '../components/SEO';
import {
  PHOTO_BOOTH_DESCRIPTION,
  PHOTO_DESIGN_OPTIONS,
} from '../constants/imageGeneration';
import { pageContentLocalStorageProperties } from '../constants/localStorage';
import { routes } from '../constants/routes';
import CreditContext from '../context/credits/context';
import { ImageGenerationProperties, PageKey } from '../enums/pageKey';
import { PhotoDesign } from '../enums/photoDesign';
import { TOP_UP_TRACKING } from '../enums/topUpTracking';
import useAutosizeTextArea from '../hooks/useAutoSizeTextArea';
import {
  checkInAppBrowser,
  useInAppBrowser,
} from '../hooks/useCheckInAppBrowser';
import { useGenerationTracking } from '../hooks/useGenerationTracking';
import useLocalStorageItem from '../hooks/useLocalStorageItem';
import { useModalState } from '../hooks/useModalState';
import SidebarLayout from '../layouts/SidebarLayout';
import ResultsLoader from '../ResultsLoader';
import ImageGenerationSchema from '../schemas/ImageGenerationSchema';
import { imageGenerationProperties } from '../types/page';
import { generateAiPrompt } from '../utils/aiImageGeneratePrompt';
import { signInUser } from '../utils/auth.utils';
import { handleResponseError as handleResponseErrorUtil } from '../utils/error.utils';
import processInput from '../utils/formInput';
import { getLocalStorageItem } from '../utils/helper';
import getLessonFormat from '../utils/lesson-format/getLessonFormat';
import { mapChatParamValues } from '../utils/page.utils';
import { scrollToResults } from '../utils/pageScroller';
import { setResponsePopulated } from '../utils/responsePopulatedUtil';
import { generateImage } from '../utils/server-side/aiImage';
import { getUserCredit } from '../utils/server-side/credits';
import { EVENTS, trackEvent } from '../utils/tracking';
const AlertModal = lazy(() => import('../components/AlertModal'));
const AuthLayout = lazy(() =>
  import('../components/AuthLayout').then((mod) => ({
    default: mod.AuthLayout,
  })),
);
const Description = lazy(() => import('../components/Description'));
const SignInBtn = lazy(() =>
  import('../components/SigninBtn').then((mod) => ({ default: mod.SignInBtn })),
);

const AUTHENTICATED_STATUS = 'authenticated';
const AUTH_LOADING_STATUS = 'loading';
const DEFAULT_PREMIUM_CREDIT_COST =
  parseInt(process.env.NX_GENERATE_PHOTO_COST) || 1;

export default function Index(): JSX.Element {
  const { status } = useSession();
  const { showConfirmationModal, setShowConfirmationModal } = useModalState({});
  const isSignedIn = status === AUTHENTICATED_STATUS;
  const isAuthLoading = status === AUTH_LOADING_STATUS;
  const { refetch: refetchUserCredit } = getUserCredit();
  const { context: creditContext } = useContext(CreditContext);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [isNoCreditsError, setIsNoCreditsError] = useState(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);
  const [isModalOpen] = useState(false);

  const [resultingImage, setResultingImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { setItemByKey, removeItemByKey } = useLocalStorageItem();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    if (!isSignedIn && !isAuthLoading) {
      const contentProperties =
        pageContentLocalStorageProperties[PageKey.photoBooth];
      contentProperties &&
        Object.keys(contentProperties).forEach(function (key) {
          removeItemByKey(contentProperties[key]);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, creditContext]);

  useEffect(() => {
    setResponsePopulated(!!resultingImage || isGenerating);
  }, [resultingImage, isGenerating]);

  const { mutateAsync, isLoading: isLoadingAiPrompt } = streamChatCompletion(
    () => {
      setFileName('Photo_Booth');
    },
  );

  function handleResponseError(error: ErrorWithCode) {
    const { isNoCreditsError, hasError, errorDetails } =
      handleResponseErrorUtil(error);
    setIsNoCreditsError(isNoCreditsError);
    setHasError(hasError);
    setErrorDetails(errorDetails);
  }

  const { mutate, isLoading: isLoadingImage } = generateImage(
    (data: { image: string }) => {
      setResultingImage(data.image);
      refetchUserCredit();
    },
    (error) => {
      handleResponseError(error);
      setIsGenerating(false);
    },
  );

  const ERROR_MODAL_TEXT =
    'An error has occurred and we have refunded your credits. Please try again later.';

  const defaultValues = {
    topic: getLocalStorageItem(ImageGenerationProperties.topic, ''),
    design: getLocalStorageItem(
      ImageGenerationProperties.design,
      '',
    ) as PhotoDesign,
  };
  const containerRef = useRef(null);

  const lessonPlannerForm = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(ImageGenerationSchema),
  });

  const {
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = lessonPlannerForm;

  const topic = watch('topic');
  const design = watch('design');

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutosizeTextArea(textAreaRef.current, topic);

  const formContents = useMemo(
    () => ({
      [ImageGenerationProperties.topic]: topic,
      [ImageGenerationProperties.design]: design,
    }),
    [topic, design],
  );
  const { trackGenerationAttempt, trackGenerationSuccess } =
    useGenerationTracking({
      pageKey: PageKey.photoBooth,
      formContents: formContents as imageGenerationProperties,
    });

  async function triggerValidation(fieldName) {
    await trigger(fieldName);
  }

  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  const { popUpData, setPopUpData } = useInAppBrowser();
  const { data: userCredit } = getUserCredit();

  async function handleSubmit() {
    if (checkInAppBrowser()) {
      setPopUpData((prev) => ({ ...prev, isOpen: true }));
      return;
    }
    for (const property in formContents) {
      setItemByKey(property, formContents[property]);
    }

    const result = await trigger();
    if (!result) {
      return;
    }

    if (!isSignedIn) {
      setIsLoginModalOpen(true);
      return;
    }

    if (DEFAULT_PREMIUM_CREDIT_COST < 2) {
      if (userCredit.paidCredits < DEFAULT_PREMIUM_CREDIT_COST) {
        setIsConfirmationModalOpen(true);
      } else {
        handleGenerate();
      }
    } else {
      setIsConfirmationModalOpen(true);
    }
  }

  function createChatParams(pageKey: PageKey, formContents, language: string) {
    const newLessonFormat = getLessonFormat(formContents, pageKey, language);
    return {
      lessonFormat: newLessonFormat,
      values: mapChatParamValues(formContents, newLessonFormat),
    };
  }

  async function generateImageWithPrompt(design: PhotoDesign) {
    const { values, lessonFormat } = createChatParams(
      PageKey.photoBooth,
      formContents,
      Language.ENGLISH,
    );

    const newAbortController = new AbortController();
    const payload = {
      chatParams: {
        values: values,
      },
      abortController: newAbortController,
      promptname: lessonFormat,
    };

    const promptResult = await generateAiPrompt(payload, mutateAsync);

    if (!promptResult.success) {
      console.error('Error generating prompt', promptResult.error);
      return;
    }

    await mutate({
      topic: promptResult.data,
      design,
    });
  }

  async function handleGenerate() {
    trackGenerationAttempt();
    setIsConfirmationModalOpen(false);
    setResultingImage(null);
    setIsGenerating(true);

    try {
      await generateImageWithPrompt(design);
      trackGenerationSuccess();
    } catch (error) {
      setHasError(true);
      setIsGenerating(false);
    }
  }

  function handleConfirmClose() {
    setShowConfirmationModal(false);
    setResultingImage(null);
    setIsGenerating(false);
    setResponsePopulated(false);
  }

  function downloadBase64Image() {
    if (!resultingImage) {
      return;
    }

    const link = document.createElement('a');
    link.href = resultingImage;
    link.download = `${fileName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handleRedirectToTopup() {
    const paymentPageUrl = routes.PRICING;
    trackEvent(EVENTS.CLICK_TOPUP_LINK, {
      from: TOP_UP_TRACKING.GENERATE_PREMIUM_PLAN_MODAL,
    });
    window.open(paymentPageUrl, '_blank');
  }

  const handleSignIn = () => {
    setIsLoginModalOpen(false);

    signInUser(null);
  };

  const banner = (
    <GenericBanner
      content={PHOTO_BOOTH_DESCRIPTION}
      isPremium={true}
      title="Photo Booth"
    />
  );
  const topicSubLabelText = 'Describe what you want the image to be';

  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoadingImage || isLoadingAiPrompt) {
      scrollToResults(resultsRef);
    }
  }, [isGenerating, resultingImage, isLoadingAiPrompt, isLoadingImage]);

  const renderResult = () => {
    return (
      isGenerating && (
        <div ref={resultsRef} className="h-full p-4">
          {!resultingImage || isLoadingAiPrompt || isLoadingImage ? (
            <ResultsLoader message="Generating Image..." />
          ) : (
            <div className="relative flex flex-col">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-denimBlue inline-block text-2xl font-semibold">
                  Here you go!
                </span>
                <Button
                  className="inline-block w-fit rounded border border-blue-500 bg-blue-500 px-5 py-2 text-sm font-medium text-white hover:bg-blue-400 focus:outline-none focus:ring-blue-500 active:bg-blue-400 disabled:cursor-not-allowed disabled:bg-blue-400 disabled:text-white disabled:opacity-60 disabled:hover:border-blue-400"
                  type="button"
                  onClick={downloadBase64Image}
                >
                  <span className="flex items-center">
                    Download
                    <ArrowDownTrayIcon className="ml-2 h-4 w-4" />
                  </span>
                </Button>
              </div>
              <img alt={topic} src={resultingImage} />
            </div>
          )}
        </div>
      )
    );
  };
  const hasFormErrors = Object.keys(errors).length > 0;
  const isGenerateButtonDisabled =
    isLoadingAiPrompt ||
    isLoadingImage ||
    (!resultingImage && isGenerating) ||
    hasFormErrors;

  return (
    <>
      <SEO pageKey={PageKey.photoBooth} />
      <AuthLayout
        banner={banner}
        containerRef={containerRef}
        renderResults={renderResult}
      >
        <Description />
        <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
          <TextAreaField
            className="col-span-full"
            error={!!errors.topic}
            errorMessage={!!errors.topic && 'Please enter the description'}
            id="topic"
            isMaxLengthReached={topic?.length >= PHOTO_BOOTH_CHAR_LIMIT}
            label="Image Description"
            maxLength={PHOTO_BOOTH_CHAR_LIMIT}
            name="topic"
            placeholder="Input the description for this image"
            showSubLabel={true}
            subLabel={topicSubLabelText}
            type="topic"
            value={topic}
            onBlur={() => {
              processInput(
                updateFieldValue,
                triggerValidation,
                'topic',
                topic?.trim() || '',
              );
            }}
            onChange={(e) =>
              processInput(
                updateFieldValue,
                triggerValidation,
                'topic',
                e.target.value,
              )
            }
          />

          <SelectField
            className="col-span-full"
            error={!!errors.design}
            errorMessage={errors.design?.message}
            id="design"
            label="Image Style"
            name="design"
            value={design}
            onChange={(value) => updateFieldValue('design', value)}
            onBlur={() => {
              triggerValidation('design');
            }}
          >
            {PHOTO_DESIGN_OPTIONS.map((design) => (
              <option key={design.value} value={design.value}>
                {design.label}
              </option>
            ))}
          </SelectField>

          <div className="col-span-full">
            <div className="flex flex-col">
              <GenerateButton
                isPremium
                buttonText="Prepare Photo!"
                creditCost={DEFAULT_PREMIUM_CREDIT_COST}
                disabled={isGenerateButtonDisabled}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </AuthLayout>
      <PopUpModal {...popUpData} setIsOpen={setPopUpData} />

      <ModalAlerts
        handleConfirmClose={handleConfirmClose}
        setShowConfirmationModal={setShowConfirmationModal}
        showConfirmationModal={showConfirmationModal}
      />

      <AlertModal
        hasDivider
        bodyText="To access this app's features, please log in with your google account."
        isOpen={isLoginModalOpen}
        titleText="Please log in to use this app."
        confirmButtonComponent={
          <SignInBtn
            handleSignIn={handleSignIn}
            width="w-full sm:w-auto justify-center"
          />
        }
        onCancel={() => setIsLoginModalOpen(false)}
      />
      <AlertModal
        hasDivider
        hideCloseIcon
        isSingleButton
        isOpen={hasError && !isNoCreditsError}
        titleText="Something went wrong"
        variant="error"
        bodyText={
          errorDetails ? (
            <ErrorModalDetails error={errorDetails} />
          ) : (
            ERROR_MODAL_TEXT
          )
        }
        onCancel={() => setHasError(false)}
        onConfirm={() => {
          if (isModalOpen) {
            setHasError(false);
          } else {
            window.location.reload();
          }
        }}
      />

      <GeneratePremiumConfirmationModal
        creditCost={DEFAULT_PREMIUM_CREDIT_COST}
        isOpen={isConfirmationModalOpen}
        pageKey={PageKey.photoBooth}
        onCancel={() => setIsConfirmationModalOpen(false)}
        onProceed={handleGenerate}
        onRedirectToTopup={handleRedirectToTopup}
      />
    </>
  );
}

Index.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
